import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
  NbLayoutModule,
  NbMediaBreakpointsService,
  NbMenuModule,
  NbMenuService,
  NbSidebarModule,
  NbSidebarService,
} from '@nebular/theme';
import type { NbMenuItem, NbSidebarState } from '@nebular/theme';
import { HeaderExternoComponent } from '../header-externo/header-externo.component';
import { AuthService } from '../../core/auth/auth.service';
import { Subscription } from 'rxjs';

const SIDEBAR_TAG = 'menu-sidebar';

/**
 * Preferência de menu recolhido, guardada no navegador de quem usa. Não é dado de sessão: não
 * sai no `logout` e não viaja para a API — é escolha de quem está na frente da tela.
 */
const LS_MENU_RECOLHIDO = 'igreja_menu_recolhido_v1';

/**
 * Abaixo desta largura o menu deixa de ocupar espaço e passa a abrir **por cima** do
 * conteúdo (`NAV-09`). Acima dela a barra é plena e fixa (`NAV-01`).
 *
 * O corte é o `lg` do Nebular, 992px, e não o `xl`: em 1024px a barra de 256px ainda deixa
 * ~740px de conteúdo, que sobra para as tabelas, e `NAV-01` quer o menu visível sempre que
 * couber. O que a spec proíbe é a faixa estreita de ícones, e essa saiu.
 */
const LARGURA_MENU_SOBREPOSTO = 'lg';

@Component({
  selector: 'app-layout-externo',
  standalone: true,
  imports: [RouterOutlet, NbLayoutModule, HeaderExternoComponent, NbSidebarModule, NbMenuModule],
  templateUrl: './layout-externo.component.html',
  styleUrl: './layout-externo.component.scss',
})
export class LayoutExternoComponent implements OnInit, OnDestroy {
  menuItems: NbMenuItem[] = [];

  /** Verdadeiro quando o menu abre por cima do conteúdo em vez de ocupar espaço. */
  sobreposto = false;

  private sub?: Subscription;
  private menuSub?: Subscription;
  private toggleSub?: Subscription;

  /**
   * Estado com que a barra nasce, passado como `[state]` do `nb-sidebar`. Não dá para usar
   * `collapse()` no `ngOnInit`: o serviço do Nebular é um `Subject` sem memória, e o
   * `nb-sidebar` filho só se inscreve depois — o pedido se perdia e a barra nascia aberta.
   * Definido uma vez só: mudar o valor depois reaplicaria o estado por cima do botão.
   */
  estadoInicial: NbSidebarState = 'expanded';

  /** Estado real da barra, acompanhado pelo `(stateChange)` do `nb-sidebar`. */
  private recolhido = false;

  constructor(
    private auth: AuthService,
    private menuService: NbMenuService,
    private sidebarService: NbSidebarService,
    private breakpointService: NbMediaBreakpointsService,
  ) {}

  ngOnInit(): void {
    this.auth.ensurePerfil().subscribe(() => this.rebuildMenu());
    this.sub = this.auth.perfil$.subscribe(() => this.rebuildMenu());
    // Em tela estreita o menu abre sobre o conteúdo: ao escolher uma opção, ele se fecha.
    this.menuSub = this.menuService.onItemClick().subscribe(() => this.recolherMenuSeSobrepoe());

    // `onToggle` só dispara no botão de menu. Fechar ao escolher um item usa `collapse`, que
    // não passa por aqui — senão navegar no celular gravaria "recolhido" como preferência.
    // Esta inscrição é anterior à do `nb-sidebar`, então roda antes de ele trocar de estado:
    // o estado que vai valer é o inverso do atual.
    this.toggleSub = this.sidebarService.onToggle().subscribe(({ tag }) => {
      if (tag && tag !== SIDEBAR_TAG) return;
      this.salvarPreferencia(!this.recolhido);
    });

    this.aplicarEstadoInicial();
  }

  /** Toda troca de estado, venha do botão, do `collapse` ao navegar ou da largura. */
  aoMudarEstado(estado: NbSidebarState): void {
    this.recolhido = estado !== 'expanded';
  }

  @HostListener('window:resize')
  aoRedimensionar(): void {
    this.aplicarModoPorLargura();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.menuSub?.unsubscribe();
    this.toggleSub?.unsubscribe();
  }

  /**
   * Na abertura vale a preferência de quem usa. Sem preferência gravada, vale o padrão do
   * aparelho: no telefone a barra nasce escondida, porque ali ela cobre o conteúdo; no
   * computador nasce aberta, porque é onde ela cabe ao lado.
   */
  private aplicarEstadoInicial(): void {
    this.sobreposto = this.telaEstreita();
    const preferencia = this.lerPreferencia();
    this.recolhido = preferencia ?? this.sobreposto;
    this.estadoInicial = this.recolhido ? 'collapsed' : 'expanded';
  }

  private aplicarEstado(): void {
    if (this.recolhido) {
      this.sidebarService.collapse(SIDEBAR_TAG);
    } else {
      this.sidebarService.expand(SIDEBAR_TAG);
    }
  }

  private telaEstreita(): boolean {
    const bp = this.breakpointService.getBreakpointsMap();
    return window.innerWidth < bp[LARGURA_MENU_SOBREPOSTO];
  }

  private lerPreferencia(): boolean | null {
    try {
      const bruto = localStorage.getItem(LS_MENU_RECOLHIDO);
      return bruto === null ? null : bruto === '1';
    } catch {
      // Navegador com armazenamento bloqueado: sem preferência, vale o padrão do aparelho.
      return null;
    }
  }

  private salvarPreferencia(recolhido: boolean): void {
    try {
      localStorage.setItem(LS_MENU_RECOLHIDO, recolhido ? '1' : '0');
    } catch {
      // Não poder guardar a preferência não é motivo para quebrar a navegação.
    }
  }

  /**
   * `NAV-03` — a barra nunca vira faixa estreita de ícones. São dois estados só: plena, ou
   * recolhida e abrindo por cima. O estado intermediário `compacted` do Nebular, de 3.5rem,
   * é o que a regra proíbe: nessa largura o rótulo não é truncado, ele some.
   */
  private aplicarModoPorLargura(): void {
    const estreito = this.telaEstreita();

    if (estreito === this.sobreposto) {
      return;
    }

    this.sobreposto = estreito;

    // Mudou a classe de aparelho (girou o telefone, redimensionou a janela). Quem já
    // escolheu um estado continua com ele; quem nunca escolheu recebe o padrão do novo
    // tamanho.
    if (this.lerPreferencia() === null) {
      this.recolhido = estreito;
      this.aplicarEstado();
    }
  }

  /** `NAV-09` — escolhida a opção, o menu sobreposto sai da frente. */
  private recolherMenuSeSobrepoe(): void {
    if (this.sobreposto) {
      this.sidebarService.collapse(SIDEBAR_TAG);
    }
  }

  /**
   * `NAV-07` — a ordem é por frequência de uso, não alfabética e não por hierarquia de
   * perfil. O que se faz toda semana vem primeiro: o catequista chega na chamada pela
   * "Minhas turmas", e "Chamadas realizadas" é a conferência que vem junto. Cadastro e
   * configuração são de início de ciclo, e ficam no fim.
   */
  private rebuildMenu(): void {
    const p = this.auth.getPerfil();
    const items: NbMenuItem[] = [{ title: 'Início', icon: 'home-outline', link: '/dashboard' }];

    // Semana a semana
    if (p?.catequista) {
      items.push({ title: 'Minhas turmas', icon: 'briefcase-outline', link: '/minhas-turmas' });
    }

    // Admin vê todas as turmas na tela; catequista/coordenação, só as suas (vínculo TurmaUsuario).
    if (p && (p.admin || p.coordenacao || p.catequista)) {
      items.push({ title: 'Chamadas realizadas', icon: 'calendar-outline', link: '/chamadas' });
    }

    // Cadastro
    if (p && (p.admin || p.coordenacao)) {
      items.push(
        { title: 'Matrículas', icon: 'people-outline', link: '/turma-pessoa' },
        { title: 'Pessoas', icon: 'person-outline', link: '/pessoas' },
        { title: 'Turmas', icon: 'book-open-outline', link: '/turmas' },
        { title: 'Acesso às turmas', icon: 'unlock-outline', link: '/turma-usuario' },
        { title: 'Pré-inscrições', icon: 'clipboard-outline', link: '/pre-inscricoes' },
      );
    }

    // Configuração
    if (p?.admin) {
      items.push(
        { title: 'Usuários', icon: 'settings-2-outline', link: '/usuarios' },
        { title: 'Integrações', icon: 'sync-outline', link: '/integracoes' },
        { title: 'Mensagens', icon: 'email-outline', link: '/mensagens' },
      );
    }

    this.menuItems = items;
  }
}
